import type { CSSObject, Theme } from '@mui/material/styles';

// Shared light/dark surfaces for card feet and filter panels. Dark mode keeps
// the near-black glass; light mode reuses the Hub card's tinted glass.
// These are plain style objects, so spread them inside an sx callback:
//   sx={(theme) => ({ ...cardFoot(theme) })}
const LIGHT_TINT = `
  radial-gradient(300px 220px at 20% 20%, rgba(56,189,248,0.22), transparent 60%),
  radial-gradient(280px 220px at 80% 30%, rgba(139,92,246,0.19), transparent 65%),
  radial-gradient(320px 260px at 50% 100%, rgba(236,72,153,0.18), transparent 70%)
`;

const DARK_CARD_FOOT = `
  radial-gradient(circle at 18% 20%, rgba(255,47,146,0.22), transparent 58%),
  radial-gradient(circle at 82% 28%, rgba(139,92,246,0.2), transparent 62%),
  radial-gradient(circle at 50% 100%, rgba(56,189,248,0.16), transparent 68%),
  linear-gradient(180deg, rgba(10,10,16,0.96), rgba(10,10,16,0.98))
`;

// The text half of a card, under the artwork.
export const cardFoot = (theme: Theme): CSSObject =>
  theme.palette.mode === 'dark'
    ? { color: '#ffffff', background: DARK_CARD_FOOT }
    : {
        color: theme.palette.text.primary,
        backgroundImage: LIGHT_TINT,
        backgroundColor: 'rgba(255, 255, 255, 0.86)',
      };

// Filter bars: a panel that matches the Hub cards rather than flat paper.
export const filterPanel = (theme: Theme): CSSObject => ({
  // Spread into sx, where borderRadius is a multiple of shape.borderRadius (8px)
  borderRadius: 2,
  ...(theme.palette.mode === 'dark'
    ? { backgroundColor: theme.palette.background.paper }
    : {
        backgroundImage: LIGHT_TINT,
        backgroundColor: 'rgba(255, 255, 255, 0.82)',
        border: '1px solid rgba(255, 255, 255, 0.85)',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.8), 0 12px 32px -14px rgba(76,58,150,0.32)',
      }),
});

// Muted text and icons inside a card foot.
export const footSecondary = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? 'rgba(255,255,255,0.7)'
    : theme.palette.text.secondary;

// Brand pink that stays readable on either foot.
export const footPink = (theme: Theme) =>
  theme.palette.mode === 'dark' ? '#ff4fa3' : theme.palette.primary.main;

// Movie poster grid. Columns are added as the screen widens, so posters stay
// about the same size on big monitors instead of stretching. Two columns on phones.
export const posterGrid = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(2, minmax(0, 1fr))',
    sm: 'repeat(auto-fill, minmax(200px, 1fr))',
  },
  // Cards keep their own height; a watched movie's extra info line
  // shouldn't stretch the rest of its row.
  alignItems: 'start',
  gap: 3,
};
